import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Send, AlertCircle, X } from "lucide-react";
import { useApp } from "@/context/AppProviders";
import { SUPPORT_EMAIL } from "@/config";

// Serverless mailto target
const HWID_REGEX = /^[A-Za-z0-9]{16}$/;

const inputBase =
  "w-full rounded-sm border bg-secondary/40 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-colors duration-200";

const FieldError = ({ field, message }) =>
  message ? (
    <p
      data-testid={`error-${field}`}
      className="mt-1.5 flex items-center gap-1.5 text-xs text-red-500"
    >
      <AlertCircle className="h-3.5 w-3.5" />
      {message}
    </p>
  ) : null;

const ContactForm = () => {
  const { t } = useApp();
  const c = t.contact;

  const [form, setForm] = useState({
    name: "",
    email: "",
    hardwareId: "",
    package: "",
    duration: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const isOrder = ["standard", "professional", "expert"].includes(form.package);

  const update = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: value };
      // Cascading: reset duration if switching to a non-order package
      if (field === "package" && value === "inquiry") next.duration = "";
      return next;
    });
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const n = { ...prev };
      delete n[field];
      return n;
    });
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = c.errors.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = c.errors.email;
    if (!form.package) next.package = c.errors.package;
    if (isOrder && !form.duration) next.duration = c.errors.duration;
    if (isOrder) {
      if (!HWID_REGEX.test(form.hardwareId)) next.hardwareId = c.errors.hardwareId;
    } else if (form.hardwareId && !HWID_REGEX.test(form.hardwareId)) {
      next.hardwareId = c.errors.hardwareId;
    }
    return next;
  };

  const buildMailto = () => {
    const tierLabel = c.packages[form.package] || "-";
    const durationLabel = form.duration ? c.durations[form.duration] : "-";
    const L = c.mail.labels;
    const body = [
      "SecureMaintPro — Nieuwe Bestelling / New Order",
      "----------------------------------------",
      `${L.client}: ${form.name} (${form.email})`,
      `${L.tier}: ${tierLabel}`,
      `${L.duration}: ${durationLabel}`,
      `${L.hardwareId}: ${form.hardwareId || "-"}`,
      "",
      `${L.message}:`,
      form.message || "-",
    ].join("\n");
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      c.mail.subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    // Fire the serverless native mailto sequence
    window.location.href = buildMailto();
    setSubmitted(true);
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setForm({
      name: "",
      email: "",
      hardwareId: "",
      package: "",
      duration: "",
      message: "",
    });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {c.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {c.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {c.subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-12 rounded-sm border border-border bg-card p-6 sm:p-10"
        >
          <form onSubmit={handleSubmit} noValidate data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {c.fields.name}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder={c.fields.namePh}
                  data-testid="input-name"
                  className={`${inputBase} ${errors.name ? "border-red-500" : "border-border"}`}
                />
                <FieldError field="name" message={errors.name} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {c.fields.email}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder={c.fields.emailPh}
                  data-testid="input-email"
                  className={`${inputBase} ${errors.email ? "border-red-500" : "border-border"}`}
                />
                <FieldError field="email" message={errors.email} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {c.fields.package}
                </label>
                <select
                  value={form.package}
                  onChange={update("package")}
                  data-testid="input-package"
                  className={`${inputBase} ${errors.package ? "border-red-500" : "border-border"}`}
                >
                  <option value="">{c.fields.packagePh}</option>
                  <option value="standard">{c.packages.standard}</option>
                  <option value="professional">{c.packages.professional}</option>
                  <option value="expert">{c.packages.expert}</option>
                  <option value="inquiry">{c.packages.inquiry}</option>
                </select>
                <FieldError field="package" message={errors.package} />
              </div>

              {/* Cascading duration — only for actual package orders */}
              {isOrder && (
                <div data-testid="duration-wrapper">
                  <label className="block text-sm font-medium mb-1.5">
                    {c.fields.duration}
                  </label>
                  <select
                    value={form.duration}
                    onChange={update("duration")}
                    data-testid="input-duration"
                    className={`${inputBase} ${errors.duration ? "border-red-500" : "border-border"}`}
                  >
                    <option value="">{c.fields.durationPh}</option>
                    <option value="monthly">{c.durations.monthly}</option>
                    <option value="annual">{c.durations.annual}</option>
                  </select>
                  <FieldError field="duration" message={errors.duration} />
                </div>
              )}

              <div className={isOrder ? "sm:col-span-2" : ""}>
                <label className="block text-sm font-medium mb-1.5">
                  {c.fields.hardwareId}
                </label>
                <input
                  type="text"
                  value={form.hardwareId}
                  onChange={update("hardwareId")}
                  placeholder={c.fields.hardwareIdPh}
                  data-testid="input-hardware-id"
                  maxLength={16}
                  className={`${inputBase} font-mono ${errors.hardwareId ? "border-red-500" : "border-border"}`}
                />
                <FieldError field="hardwareId" message={errors.hardwareId} />
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-sm font-medium mb-1.5">
                {c.fields.message}
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder={c.fields.messagePh}
                data-testid="input-message"
                className={`${inputBase} resize-none border-border`}
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              className="mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-sm bg-blue-600 px-6 text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-200 sm:w-auto"
            >
              <Send className="h-4 w-4" />
              {c.submit}
            </button>
          </form>
        </motion.div>
      </div>

      {/* Success modal */}
      {submitted && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
          onClick={resetAndClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-success"
            className="relative w-full max-w-md rounded-sm border border-border bg-card p-8 text-center"
          >
            <button
              onClick={resetAndClose}
              data-testid="success-close"
              aria-label="Close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-sm bg-blue-600/10 text-blue-500">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="font-heading mt-5 text-xl font-semibold tracking-tight">
              {c.success.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {c.success.body}
            </p>
            <button
              onClick={resetAndClose}
              className="mt-6 inline-flex h-10 items-center justify-center rounded-sm border border-border bg-secondary/50 px-5 text-sm font-medium hover:bg-secondary transition-colors duration-200"
            >
              {c.success.close}
            </button>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default ContactForm;
